import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import {StyleSheet, TextInput as RNTextInput, View} from 'react-native';
import {zodResolver} from '@hookform/resolvers/zod';
import {Icon} from '@rn-vui/themed';

import {useModalDimensions} from '../../../hooks/useModalDimensions';
import {
  ORDER_NOTES_MAX_LENGTH,
  OrderNotesFormData,
  orderNotesSchema,
} from '../../../schemas/checkoutSchemas';
import theme from '../../../style/theme';
import Button from '../../themed/Button';
import Text from '../../themed/Text';
import ValidationMessage from '../../themed/ValidationMessage';
import CrossPlatformModal from '../../ui-controls/CrossPlatformModal';
import PressableWithOpacityFeedback from '../../ui-controls/PressableWithOpacityFeedback';
import {fontFamily} from '../../ui-controls/Text';

type InputModalProps = {
  element: (props: {onPress: () => void}) => React.ReactNode;
  openAction: () => void;
  title: string;
  saveAction: (value: string) => void;
  saveText?: string;
  closeAction: () => void;
  initialValue?: string;
  label?: string;
  helperText?: string;
  placeholder?: string;
  allCaps?: boolean;
  isVisible?: boolean;
  maxLength?: number;
};

/**
 * Use this for setting single value fields
 * @param param0
 * @returns
 */
function InputModal({
  element,
  openAction,
  isVisible,
  title,
  saveAction,
  saveText = 'SAVE',
  closeAction,
  initialValue = '',
  label,
  helperText,
  placeholder = '',
  allCaps = false,
  maxLength = ORDER_NOTES_MAX_LENGTH,
}: InputModalProps) {
  const capitalisation = allCaps ? 'characters' : 'sentences';
  const {containerStyle: modalContainerStyle} = useModalDimensions({
    size: 'medium',
    constrainHeight: true,
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: {errors},
  } = useForm<OrderNotesFormData>({
    mode: 'onTouched',
    resolver: zodResolver(orderNotesSchema),
    defaultValues: {
      notes: initialValue,
    },
  });

  // Reset form when modal becomes visible
  React.useEffect(() => {
    if (isVisible) {
      reset({notes: initialValue});
    }
  }, [isVisible, initialValue, reset]);

  const onSubmit = (data: OrderNotesFormData) => {
    saveAction(data.notes || '');
    closeAction();
  };

  // Watch the notes field for character count
  const notesValue = watch('notes') || '';

  return (
    <>
      {element({onPress: openAction})}
      <CrossPlatformModal
        isVisible={isVisible ?? false}
        hasBackdrop
        backdropOpacity={0.75}
        onBackdropPress={closeAction}
        onBackButtonPress={closeAction}
        avoidKeyboard
        keyboardAwareContent
        style={styles.modal}>
        <View style={[styles.modalContent, modalContainerStyle]}>
          {/* Header with title and close icon */}
          <View style={styles.header}>
            <Text variant="heading1">{title}</Text>
            <PressableWithOpacityFeedback onPress={closeAction}>
              <Icon name="close" size={30} />
            </PressableWithOpacityFeedback>
          </View>

          {/* Helper text */}
          {helperText ? (
            <Text style={styles.helperText} variant="caption">
              {helperText}
            </Text>
          ) : null}

          {/* Form field */}
          <View style={styles.fieldGroup}>
            {label ? <Text style={styles.fieldLabel}>{label}</Text> : null}
            <Controller
              control={control}
              name="notes"
              render={({field: {onChange, onBlur, value}}) => (
                <RNTextInput
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  style={styles.textInput}
                  autoCapitalize={capitalisation}
                  placeholder={placeholder}
                  placeholderTextColor={theme.colour.text.form.textInput.placeholder}
                  autoFocus
                  multiline
                  numberOfLines={3}
                  textAlignVertical="top"
                />
              )}
            />
            <View style={styles.fieldFooter}>
              {errors.notes ? <ValidationMessage>{errors.notes.message}</ValidationMessage> : null}
              <Text style={styles.characterCount}>
                {notesValue.length}/{maxLength}
              </Text>
            </View>
          </View>

          {/* Save button */}
          <View style={styles.buttonsContainer}>
            <Button onPress={handleSubmit(onSubmit)} title={saveText} />
          </View>
        </View>
      </CrossPlatformModal>
    </>
  );
}

const styles = StyleSheet.create({
  modal: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    margin: 0,
  },
  modalContent: {
    backgroundColor: 'white',
    padding: 20,
    borderRadius: 4,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  helperText: {
    color: '#666',
    marginBottom: 16,
  },
  fieldGroup: {
    marginBottom: 8,
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 8,
  },
  textInput: {
    fontSize: 16,
    fontWeight: '400',
    fontFamily,
    paddingHorizontal: 12,
    paddingVertical: 12,
    minHeight: 80,
    borderColor: 'grey',
    borderWidth: 0.5,
    borderRadius: 4,
    color: theme.colour.text.form.textInput.primary,
    width: '100%',
  },
  fieldFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  characterCount: {
    fontSize: 12,
    color: theme.colour.text.heading.secondary,
    textAlign: 'right',
    marginLeft: 'auto',
  },
  buttonsContainer: {
    marginTop: 24,
  },
});

export default InputModal;
